import React, { useState, useEffect, useCallback } from 'react';
import { DateRange } from 'Components/DateRange';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  Button,
  Typography,
  Paper,
  Stack,
  Chip,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { filterFilledReviewSections, normalizeConceptsList, stripHtml } from './visitConcepts';

const TRUNCATE_LEN = 40;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function ExpandableCell({ value, isHtml = false }) {
  const [expanded, setExpanded] = React.useState(false);

  const displayValue = isHtml ? filterFilledReviewSections(value) : String(value || '').trim();
  if (!displayValue) return '-';

  const plainText = isHtml ? stripHtml(displayValue) : displayValue;
  const needsMore = plainText.length > TRUNCATE_LEN;

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const htmlContentSx = {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: 'text.primary',
    '& strong': { fontWeight: 700 },
    '& b': { fontWeight: 700 },
    '& p': { margin: '0 0 6px' },
  };

  if (!needsMore) {
    if (isHtml) {
      return (
        <Box sx={{ py: 0.5, width: '100%', ...htmlContentSx }} dangerouslySetInnerHTML={{ __html: displayValue }} />
      );
    }
    return (
      <Typography variant="body2" sx={{ py: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {displayValue}
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.5, width: '100%' }}>
      {expanded ? (
        isHtml ? (
          <Box sx={htmlContentSx} dangerouslySetInnerHTML={{ __html: displayValue }} />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
            {displayValue}
          </Typography>
        )
      ) : (
        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
          {`${plainText.slice(0, TRUNCATE_LEN)}...`}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.25, fontWeight: 600 }}
        onClick={handleToggle}
      >
        {expanded ? 'Show less' : 'Show more'}
      </Button>
    </Box>
  );
}

function CompanyVisitCountView({ rows, loading, totalCount, page, pageSize, onPageChange, onPageSizeChange }) {
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading visit counts…</Typography>
      </Box>
    );
  }

  // Group the current page's rows by date + company for the summary view
  const grouped = {};
  rows.forEach((r) => {
    const date = r.visit_date || 'Unknown Date';
    const company = r.company_name || 'Unknown Company';

    if (!grouped[date]) grouped[date] = {};
    if (!grouped[date][company]) grouped[date][company] = { total: 0, visited: 0, users: [], reviews: [] };

    grouped[date][company].total += 1;
    if (r.assigned_to_name) {
      grouped[date][company].users.push(r.assigned_to_name);
    }
    if (r.school_review) {
      grouped[date][company].reviews.push(`<b>${r.assigned_to_name || 'User'}</b>: ${r.school_review}`);
    }

    if (r.status === 'visited' || r.checked_in_at) {
      grouped[date][company].visited += 1;
    }
  });

  const flatData = [];
  let id = 1;
  Object.keys(grouped).sort().reverse().forEach((date) => {
    Object.keys(grouped[date]).sort().forEach((company) => {
      flatData.push({
        id: id++,
        date,
        company,
        total: grouped[date][company].total,
        visited: grouped[date][company].visited,
        users: Array.from(new Set(grouped[date][company].users)).join(', '),
        reviews: grouped[date][company].reviews.join('<br/><br/>'),
      });
    });
  });

  const cols = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      valueFormatter: ({ value }) => {
        if (!value || value === 'Unknown Date') return value;
        const [y, m, d] = value.split('-');
        return d && m && y ? `${d}-${m}-${y}` : value;
      }
    },
    { field: 'company', headerName: 'Company', flex: 1, minWidth: 120 },
    { field: 'total', headerName: 'Total Scheduled', flex: 1, minWidth: 120 },
    { field: 'visited', headerName: 'Visits Done', flex: 1, minWidth: 100 },
    {
      field: 'users',
      headerName: 'Persons',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => <ExpandableCell value={params.value || ''} />
    },
    {
      field: 'reviews',
      headerName: 'Reviews',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => <ExpandableCell value={params.value || ''} isHtml />
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      <DataGrid
        rows={flatData}
        columns={cols}
        autoHeight
        disableSelectionOnClick
        hideFooter
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f7fb',
            fontWeight: 700,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #f0f0f0',
            py: 1,
          },
        }}
      />

      {/* ── Custom Pagination Footer ── */}
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Total count summary */}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Total:{' '}
          <strong>{totalCount}</strong> visits
          {totalCount > 0 && (
            <>
              {' '}· Page{' '}
              <strong>{page + 1}</strong> of{' '}
              <strong>{Math.max(1, Math.ceil(totalCount / pageSize))}</strong>
            </>
          )}
        </Typography>

        {/* Page size + navigation */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          labelRowsPerPage="Rows per page:"
          sx={{
            '& .MuiTablePagination-toolbar': { minHeight: 40 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
            },
          }}
        />
      </Box>
    </Paper>
  );
}

export default function CompanyOverallVisits(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);          // 0-indexed for MUI
  const [pageSize, setPageSize] = useState(25);

  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 15);
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 15);

  const formatDateForInput = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [startDate, setStartDate] = useState(formatDateForInput(defaultStartDate));
  const [endDate, setEndDate] = useState(formatDateForInput(defaultEndDate));

  const loadRows = useCallback(() => {
    setLoading(true);
    const params = {
      limit: pageSize,
      pageno: page + 1,   // backend is 1-indexed
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    getRequest(GET_URL.company_visits.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || [];
          const count = response.data?.count ?? 0;
          setRows(Array.isArray(data) ? data : []);
          setTotalCount(Number(count) || 0);
        }
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, page, pageSize]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Reset to page 0 when filters change
  const handleDateChange = (val) => {
    setPage(0);
    if (val && val.start && val.end) {
      setStartDate(val.start);
      setEndDate(val.end);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        Company Overall Visits
      </Typography>

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', boxShadow: '0 6px 20px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <DateRange
              label="Visit Date Range"
              startDate={startDate}
              endDate={endDate}
              handleChange={handleDateChange}
            />
          </Grid>
        </Grid>
      </Paper>

      <CompanyVisitCountView
        rows={rows}
        loading={loading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </Box>
  );
}
